// Copyright (c) Second Decimal, Inc. All rights reserved.
// External dependencies
import _ from 'lodash';
import uiRouter from '@uirouter/angularjs';
import { upgradeModule } from '@uirouter/angular-hybrid';
import { NavigationService } from '../Layout/navigation.service';

const windowRef = window as any;

/**
 * @ngdoc overview
 * @name weissman
 * @description
 * # weissman
 *
 * Main module of the application.
 */

const weissmanAppModule = windowRef.angular
    .module('weissmanApp', [
        'ngAnimate',
        'ui.bootstrap',
        'ngNumeraljs',
        'ui.select',
        'ui.utils.masks',
        'cfp.hotkeys',
        'infinite-scroll',
        'frapontillo.bootstrap-switch',
        'xeditable',
        uiRouter,
        'ngMessages',
        'angularMoment',
        'ui.router.state.events',
        'angular-filepicker',
        'dndLists',
        'ui.bootstrap.contextMenu',
        'ngSanitize',
        'ngFileUpload',
        upgradeModule.name,
        /*'virtualRepeat'*/
    ])
    .provider('$title', $titleProvider)
    .config(config)
    .config(hackApply)
    // A bit of a hack; sometimes when constructing a modal, we're using AngularJS's provider to pass in arguments.
    // If those arguments may be optional, we want them to default to an empty object if not provided by calling code.
    .value('optionalArgs', {});
//.run(run);

// The commented-out .run(run) line is how a typical AngularJS app works. However not all Angular 2+ dependencies are
// available at that time, so instead the application is initialized in AppComponent. AppComponent will call the
// weissmanNg1Bootstrap function to finalize application initialization.
window['weissmanNg1Bootstrap'] = function () {
    if (window['disableAppStartup']) {
        return;
    }
    applyBannerColor('API-specified environment config');
    windowRef.angular.element(document.body).injector().invoke(run);
};

windowRef.angular.module('infinite-scroll').value('THROTTLE_MILLISECONDS', 1000);

hackApply.$inject = ['$provide'];

config.$inject = ['$httpProvider', '$compileProvider', '$locationProvider', '$urlServiceProvider'];

function config($httpProvider, $compileProvider, $locationProvider, $urlServiceProvider) {
    activate();

    function activate() {
        if (!windowRef.weissmanEnvironmentConfig.developerMode) {
            $compileProvider.debugInfoEnabled(false);
        } else {
            $compileProvider.debugInfoEnabled(true);
        }

        applyBannerColor('Initial environment config');

        // Restore the pre-AngularJS 1.6 behavior of routes starting with just # instead of #!
        // (see https://docs.angularjs.org/guide/migration#migrating-from-1-5-to-1-6)
        $locationProvider.hashPrefix('');

        configHeaders();
        checkDigestDebug();

        windowRef.Highcharts.setOptions({
            lang: {
                thousandsSep: ',',
                numericSymbols: ['K', 'M', 'B', 'T', 'Q', 'Q'],
            },
        });
    }

    function configHeaders() {
        const interceptor = [
            '$q',
            '$injector',
            '$rootScope',
            function ($q, $injector, $rootScope) {
                const service = {
                    request: function (config2) {
                        // Note that there could be cases of script files with /api/ somewhere in the route, but no big deal;
                        // worst case scenario is we wastefully add a small header where we didn't need it. Also, it looks like
                        // sometimes we get /api/... and sometimes just api/...; check for both cases
                        if (config2.url.lastIndexOf('/api/') >= 0 || /^api/.test(config2.url)) {
                            const authStorageService = $injector.get('authStorageService');
                            const userInstanceService = $injector.get('userInstanceService');
                            const authData = authStorageService.getAuthData();
                            if (authData) {
                                config2.headers = config2.headers || {};
                                config2.headers.Authorization = `Bearer ${authData.access_token}`;
                                config2.headers.Instances = userInstanceService.getInstancesForHeader();
                            }
                            // All HTTP requests should count as "Activity"
                            authStorageService.recordActivity('AngularJS HTTP');
                        }

                        return config2;
                    },
                    responseError: function (rejection) {
                        console.log('AuthInterceptorService - _responseError ', rejection);
                        const authStorageService = $injector.get('authStorageService');

                        // WK-7767 - A long story, but basically it's possible for a rejection to happen if a request is automatically
                        // replayed by a browser after an authorization header is expired. We don't want to consider the user logged
                        // out in this case, so only use the default 401 handling if the current auth header is still the same as it
                        // was at the time of the request (bonus, also don't worry about 401 on a request that didn't have an auth
                        // header. I don't think that'd ever happen, but if it does it's an unexpected error, not an auth error.)
                        const authData = authStorageService.getAuthData();
                        const useDefaultAuthHandling =
                            !authData ||
                            (rejection.config.headers['Authorization'] &&
                                rejection.config.headers['Authorization'] === `Bearer ${authData.access_token}`);

                        if (useDefaultAuthHandling && rejection.status === 401) {
                            if (authStorageService.authInterceptEnabled) {
                                // We expect that the rejection will *usually* be an http response object and that the API will
                                // *usually* send a descriptive message back as a string. However, if that doesn't happen, just
                                // bomb-out with a generic message.
                                let rejectionMessage = 'Your session has ended unexpectedly';
                                if (rejection.data && typeof rejection.data === 'string') {
                                    rejectionMessage = rejection.data;
                                }
                                authStorageService.logoutMessage$.next(rejectionMessage);
                            }
                            return $q.reject(rejection);
                        } else if ((!useDefaultAuthHandling && rejection.status === 401) || rejection.status === 500) {
                            // If the calling code says that it'll handle this error, don't broadcast it
                            if (rejection.config.handleCode !== rejection.status) {
                                $rootScope.$broadcast('serverErrorDetected');
                            }
                            return $q.reject(rejection);
                        } else if (rejection.status === 409) {
                            //409 Conflict
                            //The request could not be completed due to a conflict with the current state of the target resource. This code is used in situations where the user might be able to resolve the conflict and resubmit the request.
                            //The server SHOULD generate a payload that includes enough information for a user to recognize the source of the conflict.
                            //Conflicts are most likely to occur in response to a PUT request. For example, if versioning were being used and the representation being PUT included changes to a resource that conflict with those made by an earlier (third-party) request, the origin server might use a 409 response to indicate that it can't complete the request. In this case, the response representation would likely contain information useful for merging the differences based on the revision history.
                            //Source: RFC7231 Section 6.5.8
                            console.log('rejection', rejection);
                            $rootScope.$broadcast('serverErrorCode409', rejection.data);
                            return $q.reject(rejection);
                        } else if (rejection.status === 400) {
                            //400 - Bad request. Let's use this code to return any validation errors
                            console.log('rejection', rejection);
                            // If the calling code says that it'll handle this error, don't broadcast it
                            if (rejection.config.handleCode !== rejection.status) {
                                $rootScope.$broadcast('serverErrorCode400', rejection.data.message);
                            }
                            return $q.reject(rejection);
                        } else if (rejection.status === 403) {
                            //403 - Forbidden.
                            console.log('rejection', rejection);
                            // If the calling code says that it'll handle this error, don't broadcast it
                            if (rejection.config.handleCode !== rejection.status) {
                                if (rejection.config.navigationRequest) {
                                    $rootScope.$broadcast('navigationErrorCode403', rejection.data.message);
                                } else {
                                    $rootScope.$broadcast('serverErrorCode403', rejection.data.message);
                                }
                            }
                            return $q.reject(rejection);
                        } else if (rejection.status === 412) {
                            //412 - PreconditionFailed. Use by api to report concurrency errors.
                            console.log('rejection', rejection);
                            $rootScope.$broadcast('serverErrorCode412', rejection.data.message);
                            return $q.reject(rejection);
                        } else if (rejection.status === 503 || rejection.status === 502) {
                            // 503 - Service unavailable or 502 - Bad Gateway.
                            // Indicates the app is down; the index.html page should now have
                            // a friendly message to indicate what's wrong, so refresh to get it
                            window.location.reload();
                            return $q.reject(rejection);
                        } else {
                            console.log('unknown rejection status', rejection);
                            return $q.reject(rejection);
                        }
                    },
                };

                return service;
            },
        ];

        $httpProvider.interceptors.push(interceptor);
        // This interceptor adds the build timestamp to the querystring for requests for component HTML files.
        // Note that this is now only for AngularJS components; Angular2+ component HTML is now Webpack'ed
        $httpProvider.interceptors.push([
            '$window',
            function ($window) {
                return {
                    request: function (config2) {
                        // Somehow the httpProvider gets called for some requests (like uib/template/tooltip/tooltip-popup.html) which
                        // subsequently don't get resolved to real http requests. I'm not sure where in code the http call is being
                        // prevented, but it appears that adding url arguments to config.url breaks that process. So we filter on paths
                        // that end in app/[something].html so that this only applies to Weissman component html files.
                        if (/app\/.*\.html$/.test(config2.url)) {
                            // When requesting component HTML files, use a querystring param called cache-timestamp
                            // to ensure that requests are cached until the next build
                            config2.url = `${config2.url}?cache-timestamp=${$window.weissmanBuildTimestamp}`;
                            config2.cache = true;
                        } else if (config2.cache !== true) {
                            //disable IE ajax request caching
                            config2.headers['If-Modified-Since'] = 'Mon, 26 Jul 1997 05:00:00 GMT';
                            // extra
                            config2.headers['Cache-Control'] = 'no-cache';
                            config2.headers['Pragma'] = 'no-cache';
                        }
                        return config2;
                    },
                };
            },
        ]);

        $httpProvider.interceptors.push([
            '$injector',
            function ($injector) {
                return {
                    response: function (response) {
                        if (response && response.data) {
                            $injector.get('utils').convertDateStringsToDates(response.data);
                        }
                        return response;
                    },
                };
            },
        ]);

        // Disable caching by default
        if (!$httpProvider.defaults.headers.get) {
            $httpProvider.defaults.headers.get = {};
        }
    }
}

run.$inject = [
    '$rootScope',
    'layoutService',
    '$animate',
    'editableOptions',
    '$state',
    '$stateParams',
    '$location',
    'BuildTimestampService',
    '$window',
    '$transitions',
    '$title',
    '$uibModal',
    '$q',
    'toastr',
    'sd.Navigation.Service',
    'hotkeys',
    'featureFlagsService',
    '$urlService',
];

function run(
    $rootScope,
    layoutService,
    $animate,
    editableOptions,
    $state,
    $stateParams,
    $location,
    BuildTimestampService,
    $window,
    $transitions,
    $title,
    $uibModal,
    $q,
    toastr,
    navigation: NavigationService,
    hotkeys,
    featureFlagsService,
    $urlService,
) {
    // See notes in config. The IAM component is guaranteed to be running before now, so go ahead and let
    // angular-ui-router continue with URL processing.
    // $urlService.listen();
    // $urlService.sync();

    BuildTimestampService.startCheck();
    $rootScope.layoutService = layoutService;

    $rootScope.featureFlagsService = featureFlagsService;

    hotkeys.add({
        combo: 'alt+q',
        description: 'Open Quick Search',
        callback: function () {
            navigation.openQuickSearch(true, 'hotkey');
        },
        allowIn: ['INPUT', 'SELECT'],
    });

    hotkeys.add({
        combo: 'alt+s',
        description: 'Open Advanced Search',
        callback: function () {
            $location.path('/search');
        },
        allowIn: ['INPUT', 'SELECT'],
    });

    // Provide a hook for a popup window to set credentials
    window['loginFromSSO'] = function (message, grantType, request) {
        navigation.loginFromSSO = {
            message: message,
            grantType: grantType,
            request: request,
        };
    };

    // Now that an unhandled rejection throws errors in the console (since Angular 1.6), modals routinely
    // cause error messages. We could supress that with configuration, but that's a generally very bad idea
    // (see https://www.codelord.net/2017/08/20/angular-1-dot-6-s-possibly-unhandled-rejection-errors/).
    // Instead, we squash rejections by default for $uibModal here by replacing its original open function.
    // If any code still needs the original open function (because it actually needs to know when the modal
    // was dismissed), it can use rejectableOpen.
    $uibModal.rejectableOpen = $uibModal.open;
    $uibModal.open = function (...args) {
        const modal = $uibModal.rejectableOpen.apply(this, args);
        modal.result = $q(function (resolve) {
            modal.result.then(function (...args2) {
                resolve.apply(this, args2);
            });
        });
        return modal;
    };

    $rootScope.$on('serverErrorDetected', function () {
        toastr.error('An unexpected error has occurred. Please try again later.', 'Error');
    });

    $rootScope.$on('serverErrorCode403', function () {
        toastr.error(
            'You do not have permission to perform this action. If you have any questions, please contact the site administrator.',
            'Error',
        );
    });

    $rootScope.$on('serverErrorCode409', function (event, errorMessage) {
        console.log('serverErrorCode409', errorMessage);
        toastr.error(errorMessage, 'Error');
    });

    $rootScope.$on('serverErrorCode400', function (event, errorMessage) {
        if ($location.path() !== '/login') {
            toastr.error(errorMessage, 'Error');
        }
    });

    $rootScope.$on('serverErrorCode412', function (event, errorMessage) {
        toastr.error(errorMessage, 'Error');
    });

    editableOptions.theme = 'bs5';
    $animate.enabled(true);
    // Need to bind $state and $stateParams to $rootScope to access it from a template/view.
    $rootScope.$state = $state;
    $rootScope.$stateParams = $stateParams;

    $transitions.onSuccess({}, function (trans) {
        const newTitle = trans.injector().get('$title');
        // For some reason $title is sometimes an object; this hack just skips that case (the redirect
        // routes produce this behavior). There's probably a more elegant fix.
        $rootScope.$title = typeof newTitle === 'string' ? newTitle : null;
        document.title = $rootScope.$title ? `${$rootScope.$title} - PropertyPoint` : 'PropertyPoint';
        $rootScope.$breadcrumbs = $title.breadCrumbs(trans);
    });
}

// https://github.com/nonplus/angular-ui-router-title/issues/6, 2018-03-16 comment
function $titleProvider() {
    return {
        $get: $get,
    };

    function $get() {
        return {
            breadCrumbs: breadCrumbs,
        };

        function breadCrumbs(trans) {
            const $breadcrumbs = [];
            let state = trans.targetState().$state();

            while (state && state.navigable) {
                const hasTitle = _.some(state.resolvables, function (s) {
                    return s.token === '$title';
                });

                $breadcrumbs.unshift({
                    title: hasTitle ? trans.injector(state).get('$title') : state.name,
                    state: state.name,
                    stateParams: state.params,
                });

                state = state.parent;
            }

            return $breadcrumbs;
        }
    }
}

/* Copied from https://stackoverflow.com/a/33784608; fixes strange digest issue from WK-2874
 * To reproduce the issue:
 * 1. Perform a quick search, and use the enter key to initiate the search, NOT the search button
 * 2. Click on a result (must not be the entity currently on the page)
 * An error occurs in the console:
        error_handler.ts:1 ERROR Error: $rootScope:inprog $digest already in progress
        http://errors.angularjs.org/1.4.14/$rootScope/inprog?p0=%24digest
        at angular.js:68
        at beginPhase (angular.js:16742)
        at Scope.$digest (angular.js:16180)
        at Object.next (upgrade_adapter.ts:597)
        at SafeSubscriber.schedulerFn as _next (event_emitter.ts:102)
        at SafeSubscriber.__tryOrUnsub (Subscriber.ts:254)
        at SafeSubscriber.next (Subscriber.ts:204)
        at Subscriber._next (Subscriber.ts:135)
        at Subscriber.next (Subscriber.ts:95)
        at EventEmitter.Subject.next (Subject.ts:61)
 */
function hackApply($provide) {
    // Workaround for https://github.com/angular/angular.js/issues/10083
    $provide.decorator('$rootScope', [
        '$delegate',
        '$exceptionHandler',
        function ($delegate, $exceptionHandler) {
            const proto = Object.getPrototypeOf($delegate);
            const originalDigest = proto.$digest,
                originalApply = proto.$apply;
            proto.$digest = function () {
                if ($delegate.$$phase === '$digest' || $delegate.$$phase === '$apply') return;
                //console.log('Digest called');
                originalDigest.call(this);
            };
            proto.$apply = function (fn) {
                if ($delegate.$$phase === '$digest' || $delegate.$$phase === '$apply') {
                    try {
                        this.$eval(fn);
                    } catch (e) {
                        $exceptionHandler(e);
                    }
                } else {
                    //console.log('Apply called');
                    originalApply.call(this, fn);
                }
            };
            return $delegate;
        },
    ]);
}

// TODO: Consider moving this to its own file (although that could be said for basically everything in this file)
function checkDigestDebug() {
    const debugDigestFlag = sessionStorage['debugDigest'];
    if (debugDigestFlag) {
        console.log(
            'Digest debugging enabled (to disable, type easyAccess.disableDigestDebugging(); in a console and refresh the page)',
        );

        const debugDigestFlags = debugDigestFlag.split('|');

        const replaceSetInterval = function () {
            console.log('Replacing built-in setInterval function');

            const original_setInterval = window.setInterval;
            window.setInterval = function (...args) {
                if (!window['disablediag']) {
                    console.log('setInterval called (window.disablediag=true to stop this message)');
                }
                return original_setInterval.apply(this, args);
            } as any;
        };

        const replaceSetTimeout = function () {
            console.log('Replacing built-in setTimeout function');

            const original_setTimeout = window.setTimeout;
            window.setTimeout = function (...args) {
                if (!window['disablediag']) {
                    console.log('Timeout called (window.disablediag=true to stop this message)');
                }
                return original_setTimeout.apply(this, args);
            } as any;
        };

        const disableAnimations = function () {
            console.log('Disabling animations');

            windowRef.angular.module('disableNgAnimate', []).run([
                '$animate',
                function ($animate) {
                    $animate.enabled(false);
                },
            ]);

            windowRef.angular.module('disableCssAnimate', []).run(function () {
                const style = document.createElement('style');
                style.type = 'text/css';
                style.innerHTML =
                    '* {' +
                    '-webkit-transition: none !important;' +
                    '-moz-transition: none !important' +
                    '-o-transition: none !important' +
                    '-ms-transition: none !important' +
                    'transition: none !important' +
                    '}';
                document.getElementsByTagName('head')[0].appendChild(style);
            });
        };

        const enableDebugging = function () {
            document.getElementsByTagName('html')[0].classList.add('digest-debug');

            if (debugDigestFlags.indexOf('diableanim') >= 0) {
                disableAnimations();
            }

            if (debugDigestFlags.indexOf('timeout') >= 0) {
                replaceSetTimeout();
            }

            if (debugDigestFlags.indexOf('interval') >= 0) {
                replaceSetInterval();
            }
        };

        setTimeout(function () {
            // Check that our storage hack still works; whenStable never returns if the timeout poll in the session
            // storage library is left alone. As an additional side effect, I expect that if whenStable doesn't work,
            // any fix for our current digest issues won't work either, so let's clear that before proceding
            windowRef.angular.getTestability(document.body).whenStable(function () {
                console.log('whenStable check passed');
                enableDebugging();
            });
        }, 2000);
    }
}

function applyBannerColor(message) {
    console.log(message, windowRef.weissmanEnvironmentConfig.runtimeEnvironment);
    document.body.classList.remove('local-display-mode');
    document.body.classList.remove('non-prod-display-mode');
    switch (windowRef.weissmanEnvironmentConfig.runtimeEnvironment) {
        case 'local':
            document.body.classList.add('local-display-mode');
            break;
        case 'uat':
            document.body.classList.add('uat-display-mode');
            break;
        case 'prod':
            break;
        default:
            document.body.classList.add('non-prod-display-mode');
            break;
    }
}

export { weissmanAppModule };
